// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 — change what a customer pays, and see every change ever made (owner ask 2026-10-14:
// "ASP pays $30, we agreed $50 — change it and keep track of what changed").
// A new price starts on the 1st of the chosen month; earlier months keep their price. Every change is
// logged (who, when, from when, old → new, why) and audit-logged server-side.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import AppDialog from '../../../components/ui/AppDialog'
import AppButton from '../../../components/ui/AppButton'
import LoadState from '../../../components/ui/LoadState'
import Notice from '../../../components/ui/Notice'
import StatusChip from '../../../components/ui/StatusChip'
import { useToast } from '../../../components/ui/Toast'
import { AppTextField, ChoiceToggle, MoneyField, SelectField } from '../../../components/ui/FormFields'
import { DataGrid, type DataGridColumn } from '../../../components/DataGrid'
import { friendlyError } from '../../../api/errors'
import { financeKeys, getCustomerPricing, setCustomerPrice, stopCustomerBilling, type CustomerPricing } from '../../../api/finance'
import { money } from './FinanceTables'
import { monthLabel } from './CostItemDialogs'

type Change = CustomerPricing['changes'][number]

const SOURCE_LABEL: Record<Change['source'], string> = {
  profitability_v2: 'Profitability v2', current_tab: 'Current tab', existing: 'on record before the log',
}

/** 12 months back → 12 months ahead, newest first. */
function priceMonths(): string[] {
  const out: string[] = []
  const now = new Date()
  for (let i = 12; i >= -12; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1))
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
  }
  return out
}

const thisMonth = () => new Date().toISOString().slice(0, 7)
const prevMonthOf = (m: string) => {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(Date.UTC(y, mo - 2, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const historyCols: DataGridColumn<Change>[] = [
  { key: 'when', header: 'Changed', minWidth: 150, value: c => c.changed_at, render: c => new Date(c.changed_at).toLocaleString() },
  { key: 'by', header: 'By', value: c => c.changed_by },
  { key: 'from', header: 'Applies from', value: c => c.effective_from, render: c => monthLabel(c.effective_from.slice(0, 7)) },
  {
    key: 'price', header: 'Price', align: 'right', value: c => c.new_monthly_usd,
    render: c => (
      <Box>
        {c.old_monthly_usd === null ? '' : <Box component="span" sx={{ color: 'text.secondary' }}>{money(c.old_monthly_usd)} → </Box>}
        {c.change_type === 'stop' || c.new_monthly_usd === null ? <StatusChip status="stopped" tone="red" label="Billing stopped" /> : <b>{money(c.new_monthly_usd)}</b>}
      </Box>
    ),
  },
  {
    key: 'delta', header: 'Change', align: 'right', noFilter: true,
    value: c => (c.old_monthly_usd === null || c.new_monthly_usd === null ? null : c.new_monthly_usd - c.old_monthly_usd),
    render: c => {
      const now = c.new_monthly_usd
      if (c.old_monthly_usd === null) return '—'
      if (now === null) return <Box component="span" sx={{ color: 'error.main' }}>−{money(c.old_monthly_usd)}/mo</Box>
      if (now === c.old_monthly_usd) return 'same'
      return (
        <Box component="span" sx={{ color: now > c.old_monthly_usd ? 'success.main' : 'error.main' }}>
          {now > c.old_monthly_usd ? '+' : '−'}{money(Math.abs(now - c.old_monthly_usd))}/mo
        </Box>
      )
    },
  },
  { key: 'reason', header: 'Why', minWidth: 200, value: c => c.reason },
  { key: 'source', header: 'Where', value: c => SOURCE_LABEL[c.source] },
]

export default function CustomerPricingDialog({ pharmacyId, onClose }: { pharmacyId: string | null; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const months = useMemo(priceMonths, [])
  const q = useQuery({
    queryKey: financeKeys.pricing(pharmacyId ?? ''), queryFn: () => getCustomerPricing(pharmacyId!), enabled: pharmacyId !== null,
  })
  const [action, setAction] = useState<'price' | 'stop'>('price')
  const [amount, setAmount] = useState('')
  const [month, setMonth] = useState(thisMonth())
  const [reason, setReason] = useState('')
  useEffect(() => { if (pharmacyId) { setAction('price'); setAmount(''); setMonth(thisMonth()); setReason('') } }, [pharmacyId])

  const save = useMutation({
    mutationFn: () => action === 'stop'
      ? stopCustomerBilling(pharmacyId!, month, reason.trim())
      : setCustomerPrice(pharmacyId!, Math.round(Number(amount) * 100) / 100, month, reason.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financeKeys.all })
      const who = q.data?.name ?? 'Customer'
      toast.show(action === 'stop'
        ? `${who}: billing stops from ${monthLabel(month)}`
        : `${who}: ${money(Math.round(Number(amount) * 100) / 100)}/month from ${monthLabel(month)}`, 'success')
      setAmount(''); setReason('')
    },
  })

  const amt = amount.trim() === '' ? NaN : Number(amount)
  const amountOk = Number.isFinite(amt) && amt >= 0 && amt <= 1_000_000
  const valid = (action === 'stop' || amountOk) && reason.trim().length >= 5
  const p = q.data
  // The price in force just before the chosen month — what the change is measured against.
  const before = p?.schedule.filter(s => s.active && s.effective_from.slice(0, 7) <= month).sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0]?.monthly_usd ?? null
  const scheduled = p?.schedule.filter(s => s.state === 'scheduled') ?? []

  return (
    <AppDialog open={pharmacyId !== null} onClose={onClose} busy={save.isPending} maxWidth="md"
      title={p ? `What ${p.name} pays` : 'Customer price'}
      actions={<>
        <AppButton onClick={onClose} disabled={save.isPending}>Close</AppButton>
        <AppButton variant="contained" disabled={!valid || save.isPending || !p} onClick={() => { save.reset(); save.mutate() }}>
          {save.isPending ? 'Saving…' : action === 'stop' ? 'Stop billing' : 'Save new price'}
        </AppButton>
      </>}>
      <LoadState query={q} what="this customer's pricing" isEmpty={() => false} empty={null}>
        {data => (
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            <Box sx={{ fontSize: 13.5 }}>
              Now: <b>{data.ended ? 'billing stopped' : data.current_monthly_usd === null ? 'no price set' : `${money(data.current_monthly_usd)} / month`}</b>
              {data.current_since && <Box component="span" sx={{ color: 'text.secondary' }}> since {monthLabel(data.current_since.slice(0, 7))}</Box>}
              {scheduled.map(s => (
                <Box key={s.effective_from} sx={{ mt: 0.5 }}>
                  <StatusChip status="scheduled" tone="blue" label="Scheduled" />{' '}
                  {s.is_end || s.monthly_usd === null ? 'billing stops' : `${money(s.monthly_usd)} / month`} from {monthLabel(s.effective_from.slice(0, 7))}
                </Box>
              ))}
            </Box>

            {save.isError && <Notice severity="error">{friendlyError(save.error)}</Notice>}
            <ChoiceToggle ariaLabel="What to change" value={action} onChange={setAction}
              options={[{ value: 'price', label: 'Change price' }, { value: 'stop', label: 'Stop billing' }]} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {action === 'price'
                ? <MoneyField label="New monthly price" required value={amount} onChange={setAmount} error={amount !== '' && !amountOk} />
                : <Box sx={{ fontSize: 13, color: 'text.secondary', alignSelf: 'center' }}>No charge from the month you pick. Set a price again later to resume.</Box>}
              <SelectField label="Starts" value={month} onChange={setMonth}
                options={months.map(m => ({ value: m, label: monthLabel(m) + (m === thisMonth() ? ' (this month)' : '') }))} />
            </Box>
            <AppTextField label="Why (kept in the history)" required value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. agreed new price with the customer on the 10/14 call" inputProps={{ maxLength: 400 }}
              error={reason !== '' && reason.trim().length < 5} helperText="At least 5 characters." />
            {action === 'stop' && (
              <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
                Billing stops from <b>{monthLabel(month)}</b>{before !== null ? ` (was ${money(before)} / month)` : ''}. {monthLabel(prevMonthOf(month))} and
                earlier keep their price, and the customer shows as ended — not as a $0 customer.
              </Box>
            )}
            {action === 'price' && amountOk && (
              <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
                {before === null ? `${money(amt)} / month` : `${money(before)} → ${money(amt)} / month`} from <b>{monthLabel(month)}</b>
                {before !== null && amt !== before && <> ({amt > before ? '+' : '−'}{money(Math.abs(amt - before))}/month, {amt > before ? '+' : '−'}{money(Math.abs(amt - before) * 12)}/year)</>}.
                {' '}{monthLabel(prevMonthOf(month))} and earlier keep their price.
              </Box>
            )}

            <Box>
              <Box sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>History — every change, newest first</Box>
              {data.changes.length === 0
                ? <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>No changes recorded yet.</Box>
                : <DataGrid rows={data.changes} columns={historyCols} storageKey="finance-v2-price-history" getRowKey={c => c.id} dense />}
            </Box>
          </Box>
        )}
      </LoadState>
    </AppDialog>
  )
}
